import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { apiRequest, canSharePhotos, downloadPhoto, sharePhoto } from '../../utils';
import Icon from '../../components/common/Icon';
import './PublicAlbum.css';

// The albums blob container is public; we build full URLs from the stored
// blob name (same scheme the authenticated /album page uses).
const ALBUM_BASE = 'https://herculeanprod.blob.core.windows.net/albums';
const photoUrl = (name) => `${ALBUM_BASE}/${encodeURIComponent(name)}`;

/**
 * Public, login-free photo gallery for an event (shared by link).
 * Responsive masonry that looks at home on phone and desktop, a full-screen
 * lightbox with prev/next, and — per photo — a real download plus the phone's
 * own share sheet where the browser has one (both in utils, which explains the
 * cross-origin rule that makes them necessary). Read-only: there is
 * intentionally no upload affordance here.
 */
const PublicAlbum = () => {
  const { t } = useTranslation();
  const { eventId } = useParams();

  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound
  const [activeIndex, setActiveIndex] = useState(null); // lightbox index
  // The NAME of the photo currently being fetched, not a bare flag: only the button that was
  // actually pressed should look busy, or every tile on a desktop screen lights up at once.
  const [busyName, setBusyName] = useState(null);

  // A property of the browser, not of the photograph, so it is asked once rather than per tile.
  const [shareable] = useState(canSharePhotos);

  useEffect(() => {
    if (!eventId) { setStatus('notfound'); return; }
    let cancelled = false;
    setStatus('loading');
    apiRequest('GET', `/public-album/${eventId}`)
      .then((res) => {
        if (cancelled) return;
        setEvent(res.event || null);
        setPhotos(Array.isArray(res.photos) ? res.photos : []);
        setStatus('ok');
      })
      .catch(() => { if (!cancelled) setStatus('notfound'); });
    return () => { cancelled = true; };
  }, [eventId]);

  // The whole Tornea app is pinned to a phone-width column via
  // `#root { max-width: var(--app-max-width) }` (index.css). This gallery is the one
  // page meant to fill a laptop/desktop screen, so lift that cap while it's
  // mounted — and put it back on unmount so every other page stays a centered
  // mobile view, even on a laptop.
  useEffect(() => {
    const root = document.getElementById('root');
    document.body.classList.add('full-bleed-route');
    root?.classList.add('full-bleed-root');
    return () => {
      document.body.classList.remove('full-bleed-route');
      root?.classList.remove('full-bleed-root');
    };
  }, []);

  const closeLightbox = useCallback(() => setActiveIndex(null), []);
  const showPrev = useCallback(
    () => setActiveIndex((i) => (i === null ? i : (i - 1 + photos.length) % photos.length)),
    [photos.length],
  );
  const showNext = useCallback(
    () => setActiveIndex((i) => (i === null ? i : (i + 1) % photos.length)),
    [photos.length],
  );

  // Keyboard control for the lightbox (Esc / arrows).
  useEffect(() => {
    if (activeIndex === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') showPrev();
      else if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    // Lock background scroll while the lightbox is open.
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [activeIndex, closeLightbox, showPrev, showNext]);

  // Saving and sharing live in utils, because the in-app album and the team album need exactly the
  // same thing and the cross-origin rule that makes it necessary is explained there once.
  const savePhoto = useCallback(async (action, name) => {
    setBusyName(name);
    try {
      await action(photoUrl(name), name);
    } finally {
      setBusyName(null);
    }
  }, []);

  if (status === 'loading') {
    return (
      <div className="pa-page">
        <div className="pa-state">
          <div className="pa-spinner" aria-hidden="true" />
          <p>{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (status === 'notfound') {
    return (
      <div className="pa-page">
        <div className="pa-state">
          <Icon name="picture" className="pa-state-icon" />
          <p>{t('noPhotosFound')}</p>
        </div>
      </div>
    );
  }

  const title = event?.name || t('photos');
  const active = activeIndex !== null ? photos[activeIndex] : null;

  return (
    <div className="pa-page">
      {/* Hero header */}
      <header className="pa-hero">
        <div className="pa-hero-inner">
          {event?.photo_url && (
            <img className="pa-hero-logo" src={event.photo_url} alt="" />
          )}
          <div className="pa-hero-text">
            <span className="pa-hero-eyebrow">
              <Icon name="picture" className="me-1" /> {t('photos')}
            </span>
            <h1 className="pa-hero-title">{title}</h1>
            {photos.length > 0 && (
              <span className="pa-hero-count">{photos.length} {t('photos')}</span>
            )}
          </div>
        </div>
      </header>

      {/* Gallery */}
      {photos.length > 0 ? (
        <main className="pa-gallery">
          {photos.map((picture, i) => {
            const url = photoUrl(picture.name);
            return (
              <figure className="pa-tile" key={picture.name}>
                <button
                  type="button"
                  className="pa-tile-btn"
                  onClick={() => setActiveIndex(i)}
                  aria-label={t('photoPreview')}
                >
                  <img className="pa-tile-img" src={url} alt="" loading="lazy" />
                  <span className="pa-tile-overlay" />
                </button>
                <button
                  type="button"
                  className="pa-tile-download"
                  disabled={busyName === picture.name}
                  onClick={() => savePhoto(downloadPhoto, picture.name)}
                  aria-label={t('download')}
                  title={t('download')}
                >
                  <Icon name="download" />
                </button>
              </figure>
            );
          })}
        </main>
      ) : (
        <div className="pa-state">
          <Icon name="picture" className="pa-state-icon" />
          <p>{t('noPhotosFound')}</p>
        </div>
      )}

      {/* Lightbox */}
      {active && (
        <div className="pa-lightbox" onClick={closeLightbox} role="dialog" aria-modal="true">
          <button
            type="button"
            className="pa-lb-close"
            onClick={closeLightbox}
            aria-label="Close"
          >
            <Icon name="cross" />
          </button>

          {photos.length > 1 && (
            <button
              type="button"
              className="pa-lb-nav pa-lb-prev"
              onClick={(e) => { e.stopPropagation(); showPrev(); }}
              aria-label="Previous"
            >
              <Icon name="angle-left" />
            </button>
          )}

          <div className="pa-lb-stage" onClick={(e) => e.stopPropagation()}>
            <img className="pa-lb-img" src={photoUrl(active.name)} alt="" />
            <div className="pa-lb-actions">
              {/* Share leads where the browser has a sheet for it. This gallery is the link people
                  forward to everybody who was there, so it is opened on a phone more often than
                  anywhere else — and on a phone "download" ends in a folder, while "share" ends in
                  the group chat. */}
              {shareable && (
                <button
                  type="button"
                  className="pa-lb-download pa-lb-share"
                  disabled={busyName === active.name}
                  onClick={() => savePhoto(sharePhoto, active.name)}
                >
                  <Icon name="share" className="me-2" /> {t('share')}
                </button>
              )}
              <button
                type="button"
                className="pa-lb-download"
                disabled={busyName === active.name}
                onClick={() => savePhoto(downloadPhoto, active.name)}
              >
                <Icon name="download" className="me-2" /> {t('download')}
              </button>
            </div>
          </div>

          {photos.length > 1 && (
            <button
              type="button"
              className="pa-lb-nav pa-lb-next"
              onClick={(e) => { e.stopPropagation(); showNext(); }}
              aria-label="Next"
            >
              <Icon name="angle-right" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PublicAlbum;
